import React, { FunctionComponent, useCallback, useMemo } from "react";
import AssetsContext, { AssetsContextValue } from "contexts/AssetsContext";
import useFancyBearsInWallet from "hooks/useFancyBearsInWallet";
import useHoneyJarsInWallet from "hooks/useHoneyJarsInWallet";
import useConnectedAccount from "hooks/useConnectedAccount";
import { useBalance } from "wagmi";
import useHoneyBalance from "hooks/useHoneyBalance";
import useFetchTraitsInWallet from "hooks/useFetchTraitsInWallet";
import useV1sInWallet from "hooks/useV1sInWallet";
import useV2sInWallet from "hooks/useV2sInWallet";

const AssetsProvider: FunctionComponent = ({ children }) => {
  const [address] = useConnectedAccount();

  const [{ data: balanceData }] = useBalance({
    addressOrName: address,
  });

  const [honeyBalance, readHoneyBalance] = useHoneyBalance(address);

  const [v1sInWallet, readV1sInWallet] = useV1sInWallet(address);
  const [v2sInWallet, readV2sInWallet] = useV2sInWallet(address);

  const [fancyBearsInWallet, readFancyBearsInWallet] =
    useFancyBearsInWallet(address);

  const [honeyJarsInWallet, readHoneyJarsInWallet] =
    useHoneyJarsInWallet(address);

  const [traitsInWallet, fetchTraits] = useFetchTraitsInWallet(address);

  const fetchTraitsInWallet = useCallback(() => {
    if (address) {
      fetchTraits(address);
    }
  }, [address, fetchTraits]);

  const contextValue = useMemo<AssetsContextValue>(
    () => ({
      balance: balanceData?.value,
      honeyBalance,
      v1sInWallet,
      v2sInWallet,
      fancyBearsInWallet,
      honeyJarsInWallet,
      traitsInWallet,
      readV1sInWallet,
      readV2sInWallet,
      readFancyBearsInWallet,
      readHoneyJarsInWallet,
      readHoneyBalance,
      fetchTraitsInWallet,
    }),
    [
      honeyBalance,
      balanceData,
      v1sInWallet,
      v2sInWallet,
      fancyBearsInWallet,
      honeyJarsInWallet,
      traitsInWallet,
      readV1sInWallet,
      readV2sInWallet,
      readFancyBearsInWallet,
      readHoneyJarsInWallet,
      readHoneyBalance,
      fetchTraitsInWallet,
    ]
  );

  return (
    <AssetsContext.Provider value={contextValue}>
      {children}
    </AssetsContext.Provider>
  );
};

export default AssetsProvider;
